/**
 * The manual entry form: what it validates, and how its strings become the API's integers.
 *
 * Amounts are typed as text, because that is what a person types -- "4.50", "$12", "1,299.00" --
 * and become integer minor units only at the edge, through `parseMoney`. Nothing in between is
 * ever a float that someone might send.
 *
 * Messages are i18next keys, not English, so the schema can be tested without a translator.
 */

import { z } from "zod";
import type { components } from "@/api/schema";
import { exponentOf, parseMoney, type Money } from "@/lib/money";

export type ManualReceiptBody = components["schemas"]["ManualReceiptIn"];

/** Valid as money in any currency: the exponent changes the scale, never the validity. */
function isAmount(value: string): boolean {
  return parseMoney(value, "CAD") !== null;
}

const lineSchema = z.object({
  description: z
    .string()
    .trim()
    .min(1, "receipts.errors.descriptionRequired")
    .max(200, "receipts.errors.tooLong"),
  amount: z.string().trim().refine(isAmount, "receipts.errors.amountInvalid"),
});

export const manualReceiptSchema = z.object({
  store_name: z.string().trim().max(160, "receipts.errors.tooLong"),
  /** `""` when not given, otherwise `YYYY-MM-DD` from a date input. */
  purchase_date: z.string(),
  total: z
    .string()
    .trim()
    .min(1, "receipts.errors.totalRequired")
    .refine(isAmount, "receipts.errors.amountInvalid"),
  tax_label: z.string().trim().max(32, "receipts.errors.tooLong"),
  tax: z
    .string()
    .trim()
    .refine((value) => value === "" || isAmount(value), "receipts.errors.amountInvalid"),
  notes: z.string(),
  lines: z.array(lineSchema),
});

export type ManualReceiptValues = z.infer<typeof manualReceiptSchema>;

export const emptyManualReceipt: ManualReceiptValues = {
  store_name: "",
  purchase_date: "",
  total: "",
  tax_label: "GST",
  tax: "",
  notes: "",
  lines: [],
};

function minor(value: string, currency: string): number {
  const money = parseMoney(value, currency);
  // The schema has already refused anything unparseable, so this is a programming error.
  if (money === null) throw new Error(`not an amount: ${value}`);
  return money.minor;
}

/**
 * The request body for validated form values.
 *
 * The currency is sent explicitly, never left to the API's default: the amounts were scaled into
 * minor units with that currency's exponent, so the server must read them in the same one. A
 * mismatch would be wrong by a factor of a hundred for yen. Other optional fields are left out
 * rather than sent empty, so the API applies its own defaults.
 */
export function toManualReceiptBody(
  values: ManualReceiptValues,
  currency: string,
): ManualReceiptBody {
  return {
    currency,
    store_name: values.store_name,
    store_address: "",
    notes: values.notes,
    total_minor: minor(values.total, currency),
    lines: values.lines.map((line) => ({
      description: line.description,
      line_total_minor: minor(line.amount, currency),
    })),
    tax_lines:
      values.tax === ""
        ? []
        : [{ label: values.tax_label || "Tax", amount_minor: minor(values.tax, currency) }],
    ...(values.purchase_date ? { purchase_date: values.purchase_date } : {}),
  };
}

// --- review: correcting what the receipt says about itself ---------------------------------------

export type ReceiptChangesBody = components["schemas"]["ReceiptPatchIn"];

const optionalAmount = z
  .string()
  .trim()
  .refine((value) => value === "" || isAmount(value), "receipts.errors.amountInvalid");

export const detailsSchema = z.object({
  store_name: z.string().trim().max(160, "receipts.errors.tooLong"),
  store_address: z.string().trim(),
  purchase_date: z.string(),
  subtotal: optionalAmount,
  tax_lines: z.array(
    z.object({
      label: z.string().trim().min(1, "receipts.errors.taxLabelRequired").max(32),
      amount: z.string().trim().refine(isAmount, "receipts.errors.amountInvalid"),
    }),
  ),
  total: optionalAmount,
  notes: z.string(),
});

export type DetailsValues = z.infer<typeof detailsSchema>;

/** An amount as a person would type it back: `12.99`, never `12.990000001`. */
export function amountInput(money: Money | null): string {
  if (!money) return "";
  const exponent = exponentOf(money.currency);
  return (money.minor / 10 ** exponent).toFixed(exponent);
}

export function detailsFromReceipt(receipt: {
  store_name: string;
  store_address: string;
  purchase_date: string | null;
  subtotal: Money | null;
  total: Money | null;
  notes: string;
  tax_lines: { label: string; amount: Money }[];
}): DetailsValues {
  return {
    store_name: receipt.store_name,
    store_address: receipt.store_address,
    purchase_date: receipt.purchase_date ?? "",
    subtotal: amountInput(receipt.subtotal),
    tax_lines: receipt.tax_lines.map((tax) => ({
      label: tax.label,
      amount: amountInput(tax.amount),
    })),
    total: amountInput(receipt.total),
    notes: receipt.notes,
  };
}

/**
 * Every field is sent, so what the form shows is what the receipt says afterwards. An empty
 * amount or date is sent as `null`, which clears it: the receipt did not print one.
 */
export function toDetailsBody(values: DetailsValues, currency: string): ReceiptChangesBody {
  const amount = (value: string) => (value === "" ? null : minor(value, currency));
  return {
    store_name: values.store_name,
    store_address: values.store_address,
    purchase_date: values.purchase_date || null,
    subtotal_minor: amount(values.subtotal),
    total_minor: amount(values.total),
    tax_lines: values.tax_lines.map((tax) => ({
      label: tax.label,
      amount_minor: minor(tax.amount, currency),
    })),
    notes: values.notes,
  };
}
